import { FC, useRef, useState, useEffect, useMemo, memo } from 'react'
import Link from 'next/link'
import clsx from 'clsx'

import ScrollArea from 'containers/scroll-area/scroll-area'

import { IObj } from 'constants/types/common.types'

import style from './switch-toggle.module.scss'

const CONST_FADE = 20
const BORDER_WIDTH = 120
interface ISwitchToggle {
  options: string[]
  selectedOption: string
  handleToggle: (option: string) => void
  modificator?: string
  activeModificator?: string
  wrapperModificator?: string
  lineModificator?: string
  fadeModificator?: string
  labelDopParams?: IObj<string>
  isWithoutFade?: boolean
  optionsLinks?: IObj<string>
}

const SwitchToggle: FC<ISwitchToggle> = ({
  options,
  selectedOption,
  handleToggle,
  modificator,
  activeModificator,
  wrapperModificator,
  lineModificator,
  fadeModificator,
  labelDopParams,
  isWithoutFade = false,
  optionsLinks,
}: ISwitchToggle) => {
  const [config, setConfig] = useState({
    width: 0,
    leftShift: 0,
    leftBoundary: true,
    rightBoundary: isWithoutFade,
  })

  const selectedPositionRef = useRef<HTMLDivElement>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const scrollAreaRef = useRef<HTMLDivElement>(null)

  const handleLink = (e: React.MouseEvent<HTMLAnchorElement>, option: string) => {
    e.preventDefault()
    handleToggle(option)
  }

  useEffect(() => {
    const position = selectedPositionRef?.current
    if (position) {
      const widthOffset = position?.clientWidth > BORDER_WIDTH ? position?.clientWidth : 0
      scrollRef.current?.scrollTo({
        left: position.offsetLeft - position.offsetWidth + widthOffset || 0,
      })
      setConfig({
        width: position.offsetWidth,
        leftShift: position.offsetLeft,
        leftBoundary: true,
        rightBoundary: isWithoutFade,
      })
    }
  }, [selectedOption, labelDopParams, isWithoutFade])

  useEffect(() => {
    if (!scrollAreaRef?.current) return

    const handleScroll = (e: Event) => {
      const target = e.target as Element
      const isRightFade = target.scrollWidth - target.scrollLeft <= target.clientWidth + CONST_FADE
      const isLeftFade = target.scrollLeft <= CONST_FADE
      setConfig((prevConfig) => {
        return { ...prevConfig, rightBoundary: isRightFade, leftBoundary: isLeftFade }
      })
    }

    const scrollableNode = scrollAreaRef?.current
    scrollableNode.addEventListener('scroll', handleScroll)
    return () => scrollableNode.removeEventListener('scroll', handleScroll)
  }, [])

  const underlineStyle = useMemo(() => {
    return {
      width: `${config.width}px`,
      transform: `translate3d(${config.leftShift}px, 0, 0)`,
    }
  }, [config.leftShift, config.width])

  return (
    <div
      className={clsx(style.wrapperFade, {
        [style.wrapperNoRightFade]: config.rightBoundary,
        [style.wrapperNoLeftFade]: config.leftBoundary,
        [fadeModificator as string]: fadeModificator,
      })}
    >
      <ScrollArea isHiddenScrollbar customRef={scrollAreaRef}>
        <div className={clsx(style.toggle, { [wrapperModificator as string]: wrapperModificator })}>
          <div
            className={clsx(style.underline, { [lineModificator as string]: lineModificator })}
            style={underlineStyle}
            ref={scrollRef}
          />
          {options.map((option) => {
            const isActive = option === selectedOption
            const txtLink = labelDopParams ? `${option} ${labelDopParams?.[option]}` : option
            return (
              <Link
                href={optionsLinks?.[option] || '/'}
                key={option}
                rel={optionsLinks?.[option] ? '' : 'nofollow'}
                onClick={(e) => handleLink(e, option)}
              >
                <div
                  className={clsx(style.option, {
                    [style.optionActive]: isActive,
                    [activeModificator as string]: activeModificator && isActive,
                    [modificator as string]: modificator,
                  })}
                  ref={isActive ? selectedPositionRef : null}
                >
                  <p>{txtLink}</p>
                </div>
              </Link>
            )
          })}
        </div>
      </ScrollArea>
    </div>
  )
}

export default memo(SwitchToggle)
